
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import BarcodeReaderMixin from '@/components/mixins/BarcodeReaderMixin.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import { BARCODE_SCAN } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { promptVideoPermission } from '@/utils/helpers'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import 'cropperjs/dist/cropper.css'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { EBCRType } from 'smartbarcode-web-core/src/utils/enums/index'
import { IProject } from 'smartbarcode-web-core/src/utils/types'
import { mixins, Options } from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import BulkScanSwitchButton from './common/BulkScanSwitchButton.vue'

// Mixins
@Options({
  components: {
    BarcodeSymbologiesSelection,
    BulkScanSwitchButton,
    IconSwitch,
    SelectionCameraPopup,
  },
  emits: ['update:modelValue', 'update:confirm'],
  name: 'ScanComponent',
})
export default class OCRComponent extends mixins(BarcodeReaderMixin) {
  @Prop({ type: String }) readonly fieldName!: string
  @Prop({ default: EBCRType.DEFAULT }) customBarcodECameraReaderType!: EBCRType // This will override customBarcodECameraReaderType from mixins
  @Prop({ type: Boolean }) readonly isShowMultipleScan?: boolean
  @Prop({ type: [String, Number] }) readonly initialValue?: string | number
  codeReader: BrowserMultiFormatReader | null = new BrowserMultiFormatReader(
    undefined,
    BARCODE_SCAN.timeBetweenScansMillis as number
  )

  _barcodeDataCapture: BarcodeCapture | undefined
  loading = true
  video: HTMLVideoElement | null = null
  pictureWidth = 320
  pictureHeight = 320
  isCaptured = false

  confirm() {
    this.$emit('update:confirm', this.valueCode.trim())
  }

  defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.codeReader = codeReader
    const tempResult: { [key: string]: unknown } = { ...result }
    if (!tempResult?.text) {
      return
    }

    if (this.isShowMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan((tempResult.text as string) || '')
      return
    }
    this.handleScanResult((tempResult.text as string) || '')
  }

  scanditDataResult(symbology: Barcode) {
    if (this.isShowMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan((symbology?.data as string) || '')
      return
    }
    if (!symbology) return
    this.handleScanResult((symbology?.data as string) || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    if (result.length < 1) return
    const firstDetectedBarcode = result[0]?.rawValue || ''

    if (this.isShowMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan(firstDetectedBarcode)
      return
    }
    this.handleScanResult(firstDetectedBarcode)
  }

  handleScanResult(plainText: string) {
    this.valueCode = plainText
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get project(): IProject {
    return this.$store.state.project.details
  }

  async mounted() {
    // Set initial value if provided
    if (this.initialValue) {
      this.valueCode = this.initialValue.toString()
    }

    await this.initCameraSelection()
    if (this.isCustomVideoReader) {
      await promptVideoPermission()
    }

    await this.setupCameraSetting()
  }

  get isCustomVideoReader() {
    return !(this.customBarcodECameraReaderType === EBCRType.SCANDIT)
  }

  async setupCameraSetting() {
    switch (this.barcodeReaderType as EBCRType) {
      case EBCRType.SCANDIT:
        await this.initScanditReader()
        break
      case EBCRType.BARCODE_DETECTION:
        await this.initBarcodeReaderPolyFill()
        break
      default:
        await promptVideoPermission()
        await this.initZxingReader()
        break
    }
  }

  get isScanditReader() {
    return this.customBarcodECameraReaderType === EBCRType.SCANDIT
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            width: { ideal: 1000 },
            height: { ideal: 1000 },
            frameRate: {
              ideal: 30,
              max: 60,
            },
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }

    const contentDialog = this.getContentDialog(plainText)
    this.isHandleMultipleScan = true

    this.$confirm(contentDialog, this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
      closeOnClickModal: true,
      showCancelButton: true,
      dangerouslyUseHTMLString: true,
    })
      .then(() => {
        this.valueCode = this.processValueCode(plainText)
      })
      .finally(() => {
        this.isHandleMultipleScan = false
      })
  }

  processValueCode(value: string) {
    let result = ''
    if (this.valueCode.trim() !== '') {
      result = `${this.valueCode.trim()} ${value}`
    } else {
      result = value
    }
    return result.trim()
  }

  resetScannedValue(isShowConfirmation = false) {
    if (!isShowConfirmation) {
      this.valueCode = ''
      return
    }
    this.$confirm(this.$t('confirm_reset_scanned_value'), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      cancelButtonText: this.$t('cancel'),
      type: 'warning',
    }).then(() => {
      this.valueCode = ''
    })
  }
}
